"use client";

import { useState, useEffect, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Briefcase, 
  ChevronRight, 
  CheckCircle2, 
  ShieldCheck, 
  ArrowLeft,
  Mail,
  Lock,
  Building2,
  FileCheck,
  Globe,
  Star,
  Plus,
  Trash2,
  Phone,
  AlertCircle,
  Eye,
  EyeOff
} from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { api } from "@/lib/api";
import { OTPModal } from "@/components/OTPModal";
import TradeSearch from "@/components/contractor/TradeSearch";
import AreaSearch from "@/components/contractor/AreaSearch";
import { PasswordRequirements } from "@/components/ui/PasswordRequirements";
import { isStrongPassword, PASSWORD_REQUIREMENTS } from "@/lib/password";
import { Globe as WebsiteIcon } from "lucide-react";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { MediaUploader } from "@/components/ui/MediaUploader";

type Step = 1 | 2 | 3 | 4 | 5;

interface ContactEmail {
  email: string;
  type: string;
  isVerified?: boolean;
}

interface ContactPhone {
  number: string;
  type: string;
  isVerified?: boolean;
}

const CONTACT_TYPES = ["Personal", "Sales", "Support", "Emergency", "Other"];

function ContractorOnboardingForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const inviteRef = searchParams.get("ref");
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (inviteRef) {
      api.trackInviteLinkClick(inviteRef).catch(() => {});
    }
  }, [inviteRef]);

  // Verification Modal State
  const [verifying, setVerifying] = useState<{ 
    type: "email" | "phone"; 
    index: number; 
    target: string; 
  } | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: "",
    emails: [{ email: "", type: "Personal", isVerified: false }] as ContactEmail[],
    phones: [{ number: "", type: "Personal", isVerified: false }] as ContactPhone[],
    businessType: "Sole Trader",
    abn: "",
    trade: "",
    about: "",
    website: "",
    postcode: "",
    zipCodes: "", 
    serviceAreas: [] as { name: string; lat: number; lng: number; type?: string; postcode?: string }[],
    licenses: "",
    companyImages: [] as string[],
    hourlyRateMin: "",
    hourlyRateMax: "",
    fixedPricing: [] as { label: string; price: number }[],
  });
  const [newPricingEntry, setNewPricingEntry] = useState({ label: "", price: "" });

  const nextStep = () => {
    if (step === 1) {
      if (!formData.emails[0].email || !formData.password || !formData.confirmPassword) {
        setError("Primary email and password fields are required");
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords do not match");
        return;
      }
      if (!isStrongPassword(formData.password)) {
        setError(PASSWORD_REQUIREMENTS);
        return;
      }
    }
    setError(null);
    setStep(s => Math.min(s + 1, 5) as Step);
  };
  
  const prevStep = () => setStep(s => Math.max(s - 1, 1) as Step);

  const addEmail = () => setFormData(p => ({ ...p, emails: [...p.emails, { email: "", type: "Personal", isVerified: false }] }));
  const removeEmail = (index: number) => {
    if (formData.emails.length <= 1) return;
    setFormData(p => ({ ...p, emails: p.emails.filter((_, i) => i !== index) }));
  };
  const updateEmail = (index: number, val: string) => {
    const next = [...formData.emails];
    next[index].email = val;
    next[index].isVerified = false; // Reset on change
    setFormData(p => ({ ...p, emails: next }));
  };
  const updateEmailType = (index: number, val: string) => {
    const next = [...formData.emails];
    next[index].type = val;
    setFormData(p => ({ ...p, emails: next }));
  };

  const addPhone = () => setFormData(p => ({ ...p, phones: [...p.phones, { number: "", type: "Personal", isVerified: false }] }));
  const removePhone = (index: number) => {
    setFormData(p => ({ ...p, phones: p.phones.filter((_, i) => i !== index) }));
  };
  
  const updatePhone = (index: number, val: string) => {
    const next = [...formData.phones];
    next[index].number = val;
    next[index].isVerified = false; // Reset on change
    setFormData(p => ({ ...p, phones: next }));
  };
  const updatePhoneType = (index: number, val: string) => {
    const next = [...formData.phones];
    next[index].type = val;
    setFormData(p => ({ ...p, phones: next }));
  };

  const handleVerifySuccess = () => {
    if (!verifying) return;
    const { type, index } = verifying;
    if (type === "email") {
      const next = [...formData.emails];
      next[index].isVerified = true;
      setFormData(p => ({ ...p, emails: next }));
    } else {
      const next = [...formData.phones];
      next[index].isVerified = true;
      setFormData(p => ({ ...p, phones: next }));
    }
    setVerifying(null);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const filteredEmails = formData.emails.filter(e => e.email.trim() !== "");
      const filteredPhones = formData.phones.filter(p => p.number.trim() !== "");
      if (!formData.name.trim() || !formData.trade.trim()) {
        setError("Business name and primary trade are required");
        setStep(2);
        return;
      }
      if (!isStrongPassword(formData.password)) {
        setError(PASSWORD_REQUIREMENTS);
        setStep(1);
        return;
      }
      if (!formData.postcode.trim() && formData.serviceAreas.length === 0) {
        setError("Add at least one service area or headquarters postcode");
        setStep(4);
        return;
      }
      const areaPostcodes = formData.serviceAreas.map(area => area.postcode).filter(Boolean) as string[];

      const payload = {
        ...formData,
        emails: filteredEmails,
        phones: filteredPhones,
        zipCodes: Array.from(new Set([
          ...formData.zipCodes.split(","),
          formData.postcode,
          ...areaPostcodes,
        ].map(z => z.trim()).filter(Boolean))),
        serviceAreas: formData.serviceAreas,
        licenses: formData.licenses.split(",").map(l => l.trim()).filter(Boolean),
        ref: inviteRef || undefined,
      };
      await api.registerContractor(payload);
      router.push(`/contractor/login?registered=true&email=${encodeURIComponent(filteredEmails[0].email.trim())}`);
    } catch (e: any) {
      setError(e.message || "Onboarding failed. Please check your details.");
    } finally {
      setLoading(false);
    }
  };



  return (
    <main className="min-h-screen bg-mesh pb-24 pt-32 font-sans antialiased">
      {/* Verification Layer */}
      <OTPModal 
        isOpen={!!verifying}
        onClose={() => setVerifying(null)}
        onVerify={handleVerifySuccess}
        target={verifying?.target || ""}
        type={verifying?.type || "email"}
      />

      <div className="mx-auto max-w-6xl px-6">
        <Link href="/" className="inline-flex items-center gap-2 text-slate-400 font-bold hover:text-slate-600 transition-all mb-12 group">
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </Link>

        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-5 bg-red-50 border border-red-200 rounded-[2rem] text-red-700 text-sm font-bold flex items-center gap-3 shadow-sm"
          >
            <AlertCircle size={20} />
            {error}
          </motion.div>
        )}

        <div className="text-center max-w-2xl mx-auto mb-12">
          <h1 className="text-5xl font-black tracking-tight text-slate-950 mb-4 leading-[1.1]">
            Join <span className="text-emerald-600">FixerLocal.</span>
          </h1>
          <p className="text-lg font-medium text-slate-500 leading-relaxed">Scale your business with enterprise-grade maintenance opportunities.</p>
        </div>

        {/* Horizontal Step Indicator */}
        <div className="mb-14 max-w-4xl mx-auto">
          <div className="flex justify-between items-center relative">
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-100 -translate-y-1/2 z-0" />
            <div
              className="absolute top-1/2 left-0 h-0.5 bg-emerald-500 -translate-y-1/2 z-0 transition-all duration-500"
              style={{ width: `${((step - 1) / 4) * 100}%` }}
            />
            {[
              { s: 1, label: "Digital Identity" },
              { s: 2, label: "Business Credentials" },
              { s: 3, label: "Core Expertise" },
              { s: 4, label: "Market Reach" },
              { s: 5, label: "Vetting" },
            ].map((item) => {
              const isActive = step === item.s;
              const isCompleted = step > item.s;
              return (
                <div key={item.s} className="flex flex-col items-center z-10 relative">
                  <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 border-2 ${
                      isActive
                        ? "bg-slate-900 border-slate-900 text-white scale-110 shadow-lg shadow-slate-200"
                        : isCompleted
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "bg-white border-slate-200 text-slate-400"
                    }`}
                  >
                    {isCompleted ? "✓" : item.s}
                  </div>
                  <span className={`text-[10px] font-bold mt-2 uppercase tracking-wider hidden sm:block text-center ${isActive ? "text-slate-950 font-extrabold" : "text-slate-400"}`}>
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1">
          {/* Form Wizard Card */}
          <div className="max-w-3xl mx-auto w-full">
            <motion.div
              layout
              className="glass p-10 md:p-16 rounded-[4rem] border border-white/50 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] relative overflow-hidden bg-white/80 backdrop-blur-3xl"
            >
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div 
                    key="step1"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    className="space-y-10"
                  >
                    <div className="space-y-3">
                       <h2 className="text-4xl font-black text-slate-950">Digital Identity</h2>
                       <p className="text-slate-500 font-medium text-lg leading-relaxed">Let's start with your professional access credentials. You can add multiple emails for sales, support, etc.</p>
                    </div>
                    
                    <div className="space-y-8">
                      {/* Multiple Emails */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Professional Emails</label>
                            <button 
                                onClick={addEmail}
                                className="text-emerald-600 hover:text-emerald-700 text-xs font-black flex items-center gap-1 transition-colors bg-emerald-50 px-3 py-1.5 rounded-full"
                            >
                                <Plus size={14} /> Add Another
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            {formData.emails.map((emailObj, idx) => (
                                <motion.div 
                                    layout
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    key={idx} 
                                    className={`flex flex-col md:flex-row gap-3 items-end md:items-center transition-all ${emailObj.isVerified ? "opacity-60" : ""}`}
                                >
                                    <div className="relative flex-1 w-full">
                                        <Mail className={`absolute left-6 top-1/2 -translate-y-1/2 transition-colors ${emailObj.isVerified ? "text-emerald-500" : "text-slate-300"}`} size={18} />
                                        <input 
                                            type="email" 
                                            disabled={emailObj.isVerified}
                                            className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 pl-16 pr-24 text-sm font-bold focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all" 
                                            placeholder={idx === 0 ? "primary@company.com" : "sales@company.com"}
                                            value={emailObj.email}
                                            onChange={e => updateEmail(idx, e.target.value)}
                                        />
                                        
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                            {!emailObj.isVerified ? (
                                                <button 
                                                    disabled={!emailObj.email.includes("@")}
                                                    onClick={() => setVerifying({ type: "email", index: idx, target: emailObj.email })}
                                                    className="h-10 px-4 rounded-xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 disabled:opacity-20 transition-all font-sans"
                                                >
                                                    Verify
                                                </button>
                                            ) : (
                                                <div className="h-10 px-4 rounded-xl bg-emerald-50 text-emerald-600 flex items-center gap-2 border border-emerald-100 shadow-sm shadow-emerald-100/50">
                                                    <CheckCircle2 size={14} />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">Verified</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-full md:w-32">
                                        <select 
                                            disabled={emailObj.isVerified}
                                            className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-4 text-xs font-bold focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all appearance-none"
                                            value={emailObj.type}
                                            onChange={e => updateEmailType(idx, e.target.value)}
                                        >
                                            {CONTACT_TYPES.map(t => <option key={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    {idx > 0 && !emailObj.isVerified && (
                                        <button 
                                            onClick={() => removeEmail(idx)}
                                            className="h-16 w-16 rounded-2xl flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all border border-transparent hover:border-red-100 shrink-0"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Create Password</label>
                          <div className="relative group">
                            <Lock className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-emerald-600 transition-colors" size={18} />
                            <input 
                              type={showPassword ? "text" : "password"} 
                              className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 pl-16 pr-12 text-sm font-bold focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all" 
                              placeholder="••••••••"
                              value={formData.password}
                              onChange={e => setFormData({...formData, password: e.target.value})}
                            />
                            <button 
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-600"
                            >
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                          <PasswordRequirements password={formData.password} />
                        </div>
                        <div className="space-y-4">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Retype Password</label>
                          <div className="relative group">
                            <Lock className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-emerald-600 transition-colors" size={18} />
                            <input 
                              type={showPassword ? "text" : "password"} 
                              className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 pl-16 pr-12 text-sm font-bold focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all" 
                              placeholder="••••••••"
                              value={formData.confirmPassword}
                              onChange={e => setFormData({...formData, confirmPassword: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <button 
                        onClick={nextStep} 
                        className="w-full h-18 rounded-[2rem] bg-emerald-600 text-white font-black text-lg flex items-center justify-center gap-3 hover:bg-emerald-700 transition-all active:scale-[0.98] shadow-[0_20px_40px_-12px_rgba(0,0,0,0.2)] mt-6 py-5"
                    >
                      Continue to Business Details
                      <ChevronRight size={22} />
                    </button>
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div 
                    key="step2"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    className="space-y-10"
                  >
                    <div className="space-y-3">
                       <h2 className="text-4xl font-black text-slate-950">Business Credentials</h2>
                       <p className="text-slate-500 font-medium text-lg leading-relaxed">How you appear to enterprise clients. Add your primary and secondary contact numbers.</p>
                    </div>
                    
                    <div className="space-y-8">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Company Legal Name</label>
                            <div className="relative">
                                <Building2 className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                <input 
                                    className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 pl-16 pr-6 text-sm font-bold focus:border-emerald-500 outline-none transition-all" 
                                    placeholder="Fixer Pro Ltd"
                                    value={formData.name}
                                    onChange={e => setFormData({...formData, name: e.target.value})}
                                />
                            </div>
                        </div>

                        {/* Multiple Phones */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Contact Numbers</label>
                                <button 
                                    onClick={addPhone}
                                    className="text-emerald-600 hover:text-emerald-700 text-xs font-black flex items-center gap-1 transition-colors bg-emerald-50 px-3 py-1.5 rounded-full"
                                >
                                    <Plus size={14} /> Add Another
                                </button>
                            </div>
                            
                            <div className="space-y-4">
                                {formData.phones.map((phoneObj, idx) => (
                                    <motion.div 
                                        layout 
                                        key={idx} 
                                        className={`flex flex-col md:flex-row gap-3 items-end md:items-center transition-all ${phoneObj.isVerified ? "opacity-60" : ""}`}
                                    >
                                        <div className="flex-1 w-full">
                                            <PhoneInput 
                                                disabled={phoneObj.isVerified}
                                                value={phoneObj.number}
                                                onChange={val => updatePhone(idx, val)}
                                                placeholder="Business Number"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {!phoneObj.isVerified ? (
                                                <button 
                                                    disabled={phoneObj.number.length < 5}
                                                    onClick={() => setVerifying({ type: "phone", index: idx, target: phoneObj.number })}
                                                    className="h-16 px-6 rounded-2xl bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 disabled:opacity-20 transition-all font-sans"
                                                >
                                                    Verify
                                                </button>
                                            ) : (
                                                <div className="h-16 px-6 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center gap-2 border border-emerald-100 shadow-sm shadow-emerald-100/50">
                                                    <CheckCircle2 size={14} />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">Verified</span>
                                                </div>
                                            )}
                                            
                                            <div className="w-32">
                                                <select 
                                                    disabled={phoneObj.isVerified}
                                                    className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-100 px-4 text-xs font-bold focus:border-emerald-500 outline-none transition-all appearance-none"
                                                    value={phoneObj.type}
                                                    onChange={e => updatePhoneType(idx, e.target.value)}
                                                >
                                                    {CONTACT_TYPES.map(t => <option key={t}>{t}</option>)}
                                                </select>
                                            </div>

                                            {idx > 0 && !phoneObj.isVerified && (
                                                <button 
                                                    onClick={() => removePhone(idx)}
                                                    className="h-16 w-16 rounded-2xl flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all border border-transparent hover:border-red-100"
                                                >
                                                    <Trash2 size={20} />
                                                </button>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Business Structure</label>
                                <select 
                                    className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-6 text-sm font-bold focus:border-emerald-500 outline-none transition-all appearance-none"
                                    value={formData.businessType}
                                    onChange={e => setFormData({...formData, businessType: e.target.value})}
                                >
                                    <option>Sole Trader</option>
                                    <option>Partnership</option>
                                    <option>LLC / Company</option>
                                </select>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">ABN / Corporate Tax ID</label>
                                <input 
                                    className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-6 text-sm font-bold focus:border-emerald-500 outline-none transition-all" 
                                    placeholder="12 345 678 910"
                                    value={formData.abn}
                                    onChange={e => setFormData({...formData, abn: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Company Website (Optional)</label>
                            <div className="relative">
                                <WebsiteIcon className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                <input 
                                    className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 pl-16 pr-6 text-sm font-bold focus:border-emerald-500 outline-none transition-all" 
                                    placeholder="https://www.yourcompany.com"
                                    value={formData.website}
                                    onChange={e => setFormData({...formData, website: e.target.value})}
                                />
                            </div>
                        </div>

                        <div className="space-y-3">
                            <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Company Gallery Showcase (Optional)</label>
                            <MediaUploader
                              label="Upload photos/videos of your business, work, or team"
                              onUploadComplete={(urls) => setFormData({...formData, companyImages: urls})}
                              maxFiles={8}
                            />
                        </div>
                    </div>
                    
                    <div className="flex gap-4 pt-4">
                      <button onClick={prevStep} className="px-10 h-18 rounded-[2rem] bg-white border border-slate-100 text-slate-500 font-black hover:bg-slate-50 transition-all active:scale-[0.98]">Back</button>
                      <button onClick={nextStep} className="flex-1 h-18 rounded-[2rem] bg-emerald-600 text-white font-black text-lg flex items-center justify-center gap-3 hover:bg-emerald-700 transition-all active:scale-[0.98] shadow-xl">
                        Next: expertise
                        <ChevronRight size={22} />
                      </button>
                    </div>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div 
                    key="step3"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    className="space-y-10"
                  >
                    <div className="space-y-3">
                       <h2 className="text-4xl font-black text-slate-950">Core Expertise</h2>
                       <p className="text-slate-500 font-medium text-lg leading-relaxed">Showcase your specialized capabilities to fix enterprise level problems.</p>
                    </div>
                    <div className="space-y-8">
                       <div className="space-y-3">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Core Professional Trade</label>
                          <TradeSearch 
                            value={formData.trade}
                            onChange={val => setFormData({...formData, trade: val})}
                          />
                       </div>
                       <div className="space-y-3">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Hourly Rate (A$)</label>
                          <p className="text-xs text-slate-400 ml-1">Shown to customers as your typical rate range. You can change this later from your profile.</p>
                          <div className="grid grid-cols-2 gap-4">
                             <input
                                type="number"
                                min="0"
                                value={formData.hourlyRateMin}
                                onChange={e => setFormData({...formData, hourlyRateMin: e.target.value})}
                                placeholder="Min (e.g. 80)"
                                className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-6 text-sm font-bold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all"
                             />
                             <input
                                type="number"
                                min="0"
                                value={formData.hourlyRateMax}
                                onChange={e => setFormData({...formData, hourlyRateMax: e.target.value})}
                                placeholder="Max (e.g. 150)"
                                className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-6 text-sm font-bold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all"
                             />
                          </div>
                       </div>
                       <div className="space-y-3">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Fixed Price List</label>
                          <p className="text-xs text-slate-400 ml-1">Set standard prices for common jobs (e.g. call-out fee, tap replacement). You can change this later from your profile.</p>
                          <div className="space-y-2">
                             {formData.fixedPricing.map((entry, index) => (
                                <div key={index} className="flex items-center gap-3 bg-slate-50/50 border border-slate-100 rounded-2xl px-5 py-3">
                                   <span className="flex-1 text-sm font-bold text-slate-800">{entry.label}</span>
                                   <span className="text-sm font-black text-emerald-700">A${entry.price}</span>
                                   <button
                                      type="button"
                                      onClick={() => setFormData(prev => ({ ...prev, fixedPricing: prev.fixedPricing.filter((_, i) => i !== index) }))}
                                      className="text-slate-400 hover:text-rose-500"
                                   >
                                      <Trash2 size={16} />
                                   </button>
                                </div>
                             ))}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <input
                                type="text"
                                placeholder="Service (e.g. Standard Call-Out Fee)"
                                value={newPricingEntry.label}
                                onChange={(e) => setNewPricingEntry({ ...newPricingEntry, label: e.target.value })}
                                className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-6 text-sm font-bold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all"
                             />
                             <div className="flex gap-2">
                                <input
                                   type="number"
                                   min="0"
                                   placeholder="Price (A$)"
                                   value={newPricingEntry.price}
                                   onChange={(e) => setNewPricingEntry({ ...newPricingEntry, price: e.target.value })}
                                   className="flex-1 h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-6 text-sm font-bold text-slate-950 outline-none focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 transition-all"
                                />
                                <button
                                   type="button"
                                   disabled={!newPricingEntry.label || !newPricingEntry.price}
                                   onClick={() => {
                                      setFormData(prev => ({ ...prev, fixedPricing: [...prev.fixedPricing, { label: newPricingEntry.label, price: parseFloat(newPricingEntry.price) }] }));
                                      setNewPricingEntry({ label: "", price: "" });
                                   }}
                                   className="h-16 w-16 shrink-0 flex items-center justify-center bg-emerald-600 text-white rounded-2xl hover:bg-emerald-700 transition-all disabled:opacity-50"
                                >
                                   <Plus size={20} />
                                </button>
                             </div>
                          </div>
                       </div>
                       <div className="space-y-3">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Professional Portfolio Summary</label>
                          <textarea 
                            rows={4}
                            className="w-full p-8 rounded-[2rem] bg-slate-50/50 border border-slate-100 text-sm font-bold focus:border-emerald-500 outline-none transition-all resize-none leading-relaxed" 
                            placeholder="Detail your corporate experience, specializations, and typical job scale..."
                            value={formData.about}
                            onChange={e => setFormData({...formData, about: e.target.value})}
                          />
                       </div>
                    </div>
                    <div className="flex gap-4 pt-4">
                      <button onClick={prevStep} className="px-10 h-18 rounded-[2rem] bg-white border border-slate-100 text-slate-500 font-black hover:bg-slate-50 transition-all active:scale-[0.98]">Back</button>
                      <button onClick={nextStep} className="flex-1 h-18 rounded-[2rem] bg-emerald-600 text-white font-black text-lg flex items-center justify-center gap-3 hover:bg-emerald-700 transition-all active:scale-[0.98] shadow-xl">
                        Next: service areas
                        <ChevronRight size={22} />
                      </button>
                    </div>
                  </motion.div>
                )}

                {step === 4 && (
                  <motion.div 
                    key="step4"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    className="space-y-10"
                  >
                    <div className="space-y-3">
                       <h2 className="text-4xl font-black text-slate-950">Market Reach</h2>
                       <p className="text-slate-500 font-medium text-lg leading-relaxed">Define your operational logistics and dispatch regions.</p>
                    </div>
                    <div className="space-y-8">
                       <div className="space-y-3">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Service Locations</label>
                          <AreaSearch 
                            selectedAreas={formData.serviceAreas}
                            onAdd={area => setFormData(p => ({ ...p, serviceAreas: [...p.serviceAreas, area] }))}
                            onRemove={name => setFormData(p => ({ ...p, serviceAreas: p.serviceAreas.filter(a => a.name !== name) }))}
                          />
                       </div>
                       <div className="space-y-3">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Headquarters Postcode</label>
                          <input 
                            className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-8 text-sm font-bold focus:border-emerald-500 outline-none transition-all" 
                            placeholder="e.g. 2000"
                            value={formData.postcode}
                            onChange={e => setFormData({...formData, postcode: e.target.value})}
                          />
                       </div>
                    </div>
                    <div className="flex gap-4 pt-4">
                      <button onClick={prevStep} className="px-10 h-18 rounded-[2rem] bg-white border border-slate-100 text-slate-500 font-black hover:bg-slate-50 transition-all active:scale-[0.98]">Back</button>
                      <button onClick={nextStep} className="flex-1 h-18 rounded-[2rem] bg-emerald-600 text-white font-black text-lg flex items-center justify-center gap-3 hover:bg-emerald-700 transition-all active:scale-[0.98] shadow-xl">
                        Next: compliance
                        <ChevronRight size={22} />
                      </button>
                    </div>
                  </motion.div>
                )}

                {step === 5 && (
                  <motion.div 
                    key="step5"
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ type: "spring", damping: 25, stiffness: 200 }}
                    className="space-y-10"
                  >
                    <div className="space-y-3">
                       <h2 className="text-4xl font-black text-slate-950">Digital Vetting</h2>
                       <p className="text-slate-500 font-medium text-lg leading-relaxed">Instant compliance validation for our elite partner network.</p>
                    </div>
                    <div className="space-y-8">
                       <div className="p-8 rounded-[2rem] bg-emerald-50/50 border border-emerald-100/50 flex gap-5">
                          <ShieldCheck className="text-emerald-600 shrink-0 mt-1" size={28} />
                          <div className="space-y-1">
                            <p className="text-sm font-black text-emerald-950 uppercase tracking-wider">Fast-Track Approval Enabled</p>
                            <p className="text-sm font-medium text-emerald-800 leading-relaxed italic">
                                Your data will be processed through our automated verification layer for instantaneous network access.
                            </p>
                          </div>
                       </div>
                       <div className="space-y-3">
                          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Professional License Identifiers (CSV)</label>
                          <input 
                            className="w-full h-16 rounded-2xl bg-slate-50/50 border border-slate-100 px-8 text-sm font-bold focus:border-emerald-500 outline-none transition-all" 
                            placeholder="LIC-PRO-998877, BUI-STD-112233"
                            value={formData.licenses}
                            onChange={e => setFormData({...formData, licenses: e.target.value})}
                          />
                       </div>
                       <div className="flex items-center gap-5 p-8 rounded-[2rem] border-2 border-dashed border-slate-200 bg-slate-50/30 cursor-pointer hover:bg-white hover:border-emerald-500/50 transition-all group">
                          <div className="h-12 w-12 rounded-2xl bg-white border border-slate-100 flex items-center justify-center text-slate-400 group-hover:text-emerald-600 transition-colors shadow-sm">
                            <FileCheck size={24} />
                          </div>
                          <div className="space-y-1">
                            <span className="block text-sm font-black text-slate-900 uppercase tracking-widest">Insurance Liability Certification</span>
                            <span className="block text-xs font-medium text-slate-400">Confirm coverage &gt; $5,000,000 AUD</span>
                          </div>
                       </div>
                    </div>
                    <div className="flex gap-4 pt-4">
                      <button onClick={prevStep} className="px-10 h-18 rounded-[2rem] bg-white border border-slate-100 text-slate-500 font-black hover:bg-slate-50 transition-all active:scale-[0.98]">Back</button>
                      <button 
                        onClick={handleSubmit} 
                        disabled={loading}
                        className="flex-1 h-18 rounded-[2rem] bg-emerald-600 text-white font-black text-lg flex items-center justify-center gap-3 hover:bg-emerald-700 transition-all active:scale-[0.98] shadow-[0_20px_40px_-12px_rgba(59,130,246,0.3)] disabled:opacity-50"
                      >
                        {loading ? "Initializing..." : "Finalize Registration"}
                        <ChevronRight size={22} />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Decorative side badge */}
              <motion.div 
                initial={{ opacity: 0, rotate: -45 }}
                animate={{ opacity: 0.03, rotate: 0 }}
                className="absolute -top-10 -right-10 pointer-events-none"
              >
                <Star size={300} />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default function ContractorOnboardingPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-mesh" />}>
      <ContractorOnboardingForm />
    </Suspense>
  );
}

